let uniqueName = 0;

// for tests only
// eslint-disable-next-line no-underscore-dangle
export const _resetUniqueName = (value = 0) => {
  uniqueName = value;
};

// todo this is a temporary polyfill while we figure out ES2020 in Glu
export const fromEntries = (entries: { [key: string]: any }) =>
  entries.reduce(
    (memo: { [key: string]: any }, [key, value]: [string, any]) => ({
      ...memo,
      [key]: value,
    }),
    {}
  );

// compareTo function for [name, index] that sorts by index
export type TSortByIndexTuple = [string, number];
export const sortByIndex = (a: TSortByIndexTuple, b: TSortByIndexTuple) => (a[1] < b[1] ? -1 : 1);

/**
 * Converts an array of initial data into a map of unique keys to indices
 * @param {string} name - The name to use as the root for the unique keys
 * @param {array} initialData - An array of initial entries
 * @returns {Object<string, number>} The resultant map
 */

export function createNameToIndexMap<D>(
  name: string,
  initialData: D[] = []
): { [key: string]: number } {
  return initialData.reduce(
    (memo, value, index) => ({
      ...memo,
      [`${name}-${uniqueName++}`]: index, // eslint-disable-line no-plusplus
    }),
    {}
  );
}

/**
 * Updates a nameToIndexMap based on external changes to the data
 * @param {string} name - The name to use as the root for the unique keys
 * @param {Object<string, number>} prevMap - The previous iteration of the map
 * @param {array} data - The updated data
 * @returns {Object<string, number>} The resultant map
 */
export function patchNameToIndexMap<D>(
  name: string,
  prevMap: { [key: string]: number },
  data: D[]
) {
  const maxIndex = data.length - 1;
  const prevMapValues = Object.values(prevMap);
  const prevMaxIndex = prevMapValues.length ? Math.max(...Object.values(prevMap)) : -1;

  if (prevMaxIndex === maxIndex) {
    return prevMap;
  }

  const sortedEntries = Object.entries(prevMap).sort(sortByIndex);

  return fromEntries(
    [...new Array(data.length)].map((_, index) => [
      sortedEntries[index]?.[0] || `${name}-${uniqueName++}`, // eslint-disable-line no-plusplus
      index,
    ])
  );
}

/**
 * Maps the array-based external representation of the list data to the internal
 * object structure with unique keys
 * @param {object<string, number>} nameToIndexMap - Mapping of names to indices
 * @param {array} dataArray - The external data array
 * @returns {Object} An object of the same data with unique keys
 */
export function mapPublicDataToPrivate(nameToIndexMap: Record<string, number>, dataArray: any) {
  const nameArray = Object.entries(nameToIndexMap).reduce(
    (memo: { [key: number]: string }[], [name, index]) => {
      memo[index] = name; // eslint-disable-line no-param-reassign

      return memo;
    },
    []
  );

  return dataArray.reduce((privateMemo: { [key: string]: string }, value: any, index: number) => {
    const keyName = nameArray[index] as keyof typeof privateMemo;
    return {
      ...privateMemo,
      [keyName]: value,
    };
  }, {});
}

/**
 * The reverse of the above function.
 * Maps the internal object structure of the data to the public array format
 * @param {object<string, number>} nameToIndexMap - Mapping of names to indices
 * @param {Object} dataObject - The internally mapped data object
 * @returns {array} The data array as expected externally
 */
export function mapPrivateDataToPublic<D = any>(
  nameToIndexMap: { [key: string]: number },
  dataObject?: D
) {
  return Object.entries(nameToIndexMap).reduce((publicArray: D[], [name, index]) => {
    publicArray[index] = dataObject?.[name as keyof D] as any; // eslint-disable-line no-param-reassign

    return publicArray;
  }, []);
}
